import {getState} from '@shopify/theme-cart';
import {formatMoney} from '@shopify/theme-currency';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import clsx from 'clsx';
import {useEffect, useState} from 'preact/hooks';
import React, {createRef, ElementType, MouseEvent, RefObject} from 'react';
import 'wicg-inert';
import defaultTheme from './mini-cart.module.pcss';
interface InertHTMLElement extends HTMLElement {
  inert: boolean;
}

export type Props = {
  checkoutUrl: string;
  successMessage: string;
  continueShoppingButton: string;
  emptyButton: string;
  emptyMessage: string;
  className?: string;
  bagLinkText?: string;
  bagUrl?: string;
  products: CartItem[];
  cartQuantity?: number;
  lockTarget?: RefObject<HTMLElement>;
  returnFocusElement?: HTMLElement;
  productFormFocusSelector?: string;
  cartLinkFocusSelector?: string;
  quantityLabel: string;
  inertElementSelector: string;
  isLoaded?: boolean;
  total: number;
  totalLabel: string;
  /**
   * Checkout link text
   * @default 'Continue to checkout'
   */
  checkoutLinkText?: string;
  /**
   * Close action text
   * @default 'Close'
   */
  closeText?: string;
  /**
   * Should display on render
   * @default false
   */
  isOpen?: boolean;
  /**
   * Element node
   * @default 'aside'
   */
  as?: ElementType;
  theme?: {
    root?: string;
    message?: string;
    close?: string;
    closeLabel?: string;
    container?: string;
    open?: string;
    checkoutButton?: string;
    bagButton?: string;
    placeholder?: string;
    placeholderItem?: string;
    placeholderImage?: string;
    placeholderDetails?: string;
    placeholderTitle?: string;
    placeholderQuantity?: string;
    placeholderTotal?: string;
    productList?: string;
    productCard?: string;
    productCardLink?: string;
    productDetails?: string;
    productTitle?: string;
    productPrice?: string;
    productImage?: string;
    productQuantity?: string;
    actionsWrapper?: string;
    emptyWrapper?: string;
    emptyMessage?: string;
    emptyButton?: string;
    continueShoppingButton?: string;
    total?: string;
  };
};

export const MiniCart = ({
  as: ComponentProp = 'aside',
  isLoaded = false,
  isOpen = false,
  closeText = 'Close this modal',
  checkoutLinkText,
  successMessage,
  continueShoppingButton,
  emptyButton,
  emptyMessage,
  checkoutUrl,
  className,
  theme = defaultTheme,
  bagLinkText,
  bagUrl,
  products = [],
  cartQuantity,
  lockTarget,
  returnFocusElement,
  productFormFocusSelector = '[data-ajaxadd-button]',
  cartLinkFocusSelector = '[data-cartlink]',
  quantityLabel,
  inertElementSelector = '[data-body-inert]',
  total,
  totalLabel,
  ...rest
}: Props) => {
  const [open, setOpen] = useState(isOpen);
  const [loaded, setLoaded] = useState(isLoaded);
  const [drawerProducts, setDrawerProducts] = useState(products);
  const [currentQuantity, setCurrentQuantity] = useState(cartQuantity);
  const [returnFocus, setReturnFocus] = useState(returnFocusElement);
  const [cartTotal, setCartTotal] = useState(total);
  const inertElement = document.querySelector(
    inertElementSelector,
  ) as InertHTMLElement;
  lockTarget = createRef<HTMLElement>();

  useEffect(() => {
    window.addEventListener('cart:updated', handleUpdate as EventListener);
    window.addEventListener('mini-cart:toggle', handleToggle as EventListener);

    return () => {
      window.removeEventListener('cart:updated', openDrawer as EventListener);
      window.removeEventListener(
        'mini-cart:toggle',
        toggleDrawer as EventListener,
      );
    };
  }, [open, currentQuantity]);

  const closeDrawer = () => {
    setBodyInert(false);

    if (lockTarget && lockTarget.current) {
      enableBodyScroll(lockTarget.current);
    }

    if (returnFocus) {
      returnFocus.focus();
    }

    setOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Close the drawer when escape is pressed anywhere within the element
    if (event.keyCode === 27) {
      closeDrawer();
    }
  };

  const handleCloseKeyDown = (event: React.KeyboardEvent) => {
    // Valid close keys are enter/escape/spacebar, respectively
    const closeKeys = [13, 27, 32];

    if (closeKeys.includes(event.keyCode)) {
      closeDrawer();
    }
  };

  const handleClick = (event: MouseEvent) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    closeDrawer();
  };

  const handleUpdate = () => {
    const form = document.querySelector(
      productFormFocusSelector,
    ) as HTMLElement;

    if (form) {
      setReturnFocus(form);
    }

    openDrawer();
  };

  const handleToggle = () => {
    const cartLink = document.querySelector(
      cartLinkFocusSelector,
    ) as HTMLElement;

    if (cartLink) {
      setReturnFocus(cartLink);
    }

    openDrawer();
  };

  const openDrawer = () => {
    setBodyInert(true);
    setLoaded(false);

    getState().then((data: Cart) => {
      setLoaded(true);
      setDrawerProducts(data.items);
      setCurrentQuantity(data.item_count);
      setCartTotal(data.total_price);
    });

    setOpen(true);

    if (lockTarget && lockTarget.current) {
      disableBodyScroll(lockTarget.current, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute('data-body-scroll-lock-ignore') !== null) {
              return true;
            }

            const parent = el.parentElement;

            if (parent) {
              el = parent;
            }
          }
          return false;
        },
      });
      lockTarget.current.focus();
    }
  };

  const toggleDrawer = () => {
    open ? closeDrawer() : openDrawer();
  };

  const createPlaceholders = () => {
    const placeholders = [];

    const placeholderItem = (
      <li className={theme.placeholderItem}>
        <div className={theme.placeholderImage} />
        <div className={theme.placeholderDetails}>
          <div className={theme.placeholderTitle} />
          <div className={theme.placeholderQuantity} />
        </div>
      </li>
    );

    for (let i = 0; i < 3; i++) {
      placeholders.push(placeholderItem);
    }

    return placeholders;
  };

  const setBodyInert = (state = true) => {
    if (inertElement) {
      inertElement.inert = state;
    }
  };

  const classes = clsx(
    theme.root,
    {
      [theme.open as any]: open,
    },
    className,
  );

  return (
    <ComponentProp
      className={classes}
      onClick={handleClick}
      onKeyDown={handleCloseKeyDown}
      {...rest}
      ref={lockTarget}
      role="dialog"
      tabindex="-1"
    >
      <div className={theme.container}>
        <button
          className={theme.close}
          onClick={closeDrawer}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
        >
          <span className={theme.closeLabel}>{closeText}</span>
        </button>
        <div className={theme.message}>{successMessage}</div>

        {!loaded && (
          <React.Fragment>
            <ul className={theme.placeholder}>{createPlaceholders()}</ul>
            <div className={theme.placeholderTotal} />
          </React.Fragment>
        )}

        {loaded && drawerProducts.length > 0 && (
          <React.Fragment>
            <ul className={theme.productList} data-body-scroll-lock-ignore>
              {drawerProducts.map((product, productIndex) => {
                return (
                  <li key={productIndex} className={theme.productCard}>
                    <a className={theme.productCardLink} href={product.url}>
                      <img
                        className={theme.productImage}
                        src={product.featured_image.url}
                        alt={product.featured_image.alt}
                        height="100"
                        width="100"
                      />

                      <div className={theme.productDetails}>
                        <span className={theme.productTitle}>
                          {product.title}
                        </span>
                        <span className={theme.productQuantity}>
                          {quantityLabel}&nbsp;{product.quantity}
                        </span>
                      </div>

                      <span className={theme.productPrice}>
                        {formatMoney(
                          product.price * product.quantity,
                          window.theme.moneyFormat,
                        )}
                      </span>
                    </a>
                  </li>
                );
              })}
            </ul>

            <span className={theme.total}>
              {totalLabel}&nbsp;
              <b>{formatMoney(cartTotal, window.theme.moneyFormat)}</b>
            </span>

            <div className={theme.actionsWrapper}>
              {bagLinkText && bagUrl && (
                <a href={bagUrl} className={theme.bagButton}>
                  {bagLinkText}&nbsp;
                  {currentQuantity && <span>({currentQuantity})</span>}
                </a>
              )}

              {checkoutLinkText && checkoutUrl && (
                <a href={checkoutUrl} className={theme.checkoutButton}>
                  {checkoutLinkText}
                </a>
              )}
            </div>

            <div className={theme.actionsWrapper}>
              <button
                className={theme.continueShoppingButton}
                onClick={closeDrawer}
              >
                {emptyButton}
              </button>
            </div>
          </React.Fragment>
        )}

        {loaded && drawerProducts.length === 0 && (
          <div>
            <span className={theme.emptyMessage}>{emptyMessage}</span>
            <button className={theme.emptyButton} onClick={closeDrawer}>
              {emptyButton}
            </button>
          </div>
        )}
      </div>
    </ComponentProp>
  );
};
